import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import SmsIcon from '@mui/icons-material/Sms';
import UndoIcon from '@mui/icons-material/Undo';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import FinancingPanel from './FinancingPanel';
import FinancingStatusBadge from './FinancingStatusBadge';
import { OPEN_STATUSES, formatTime, resursStatusLabel } from './resursLabels';

/** Resurs financing card on the invoice view: status, the link while open, and its actions. */
const ResursFinancingPanel = ({ t, invoice, onInvoiceUpdated, onNotify, onRefund, canManage }) => {
  const payment = invoice?.resurs_payment;
  const [busy, setBusy] = useState(null);
  if (!payment) return null;

  const open = OPEN_STATUSES.includes(payment.status);
  const attention = payment.needs_attention && payment.status === 'paid' ? t('financing.needsAttention') : '';

  const post = async (kind, url) => {
    setBusy(kind);
    try {
      const response = await axios.post(url);
      onInvoiceUpdated(response.data);
      if (kind === 'resend') onNotify(t('resurs.smsResent'), 'success');
    } catch (err) {
      onNotify(err.response?.data?.error || t('financing.actionFailed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const actions = [];
  if (open && canManage) {
    actions.push(
      <Button
        key="resend"
        size="small"
        variant="outlined"
        startIcon={busy === 'resend' ? <CircularProgress size={14} /> : <SmsIcon />}
        disabled={!!busy}
        onClick={() => post('resend', `/api/invoices/${invoice.id}/resurs-financing-resend/`)}
      >
        {t('resurs.resendSms')}
      </Button>,
      <Button
        key="cancel"
        size="small"
        variant="outlined"
        color="error"
        startIcon={busy === 'cancel' ? <CircularProgress size={14} /> : null}
        disabled={!!busy}
        onClick={() => post('cancel', `/api/invoices/${invoice.id}/financing-cancel/`)}
        data-testid="resurs-panel-cancel"
      >
        {t('financing.cancelRequest')}
      </Button>,
    );
  }
  if (attention && canManage) {
    actions.push(
      <Button key="refund" size="small" variant="outlined" color="warning" startIcon={<UndoIcon />} onClick={onRefund}>
        {t('resurs.refund')}
      </Button>,
    );
  }

  return (
    <FinancingPanel
      testId="resurs-panel"
      title={t('resurs.title')}
      badge={<FinancingStatusBadge status={payment.status} label={resursStatusLabel(t, payment.status)} data-testid="resurs-status" />}
      subtitle={payment.status === 'paid' || payment.status === 'refunded' ? payment.method_label : ''}
      attention={attention}
      details={[
        { label: t('resurs.sentTo'), value: open ? payment.phone_masked : '' },
        { label: t('resurs.validUntil'), value: open ? formatTime(payment.expires_at) : '' },
        { label: t('resurs.applicationReference'), value: payment.application_reference },
      ]}
      linkUrl={open ? payment.customer_url : ''}
      onCopied={() => onNotify(t('financing.linkCopied'), 'success')}
      actions={actions.length ? actions : null}
    />
  );
};

export default withTranslation()(ResursFinancingPanel);
